import queryString from 'query-string';
import { AxiosRequestConfig } from 'axios';

import { SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';
import { defaultPulseCheckApi, pulseCheck, reportingApi } from '~src/services/ApiController';
import {
  GET_SURVEY_REQUEST,
  GET_SURVEY_SUCCESS,
  GET_SURVEY_FAILED,
  SAVE_TOPICS_FOR_SURVEY_REQUEST,
  SAVE_TOPICS_FOR_SURVEY_SUCCESS,
  SAVE_TOPICS_FOR_SURVEY_FAILED,
  RESET_SURVEYS,
  GetSurveys,
  AddSurvey,
  AddManagerSurvey,
  CreateUserPullSurvey,
  GetPulseSurveys,
  GetGroupSurveys,
  GetPulseSurveysSearch,
  GetGroupSurveysSearch,
  CopySurvey,
  DeleteSurvey,
  EditSurvey,
  GetPeopleSurveysSearch,
  GetPeopleSurveys,
  CreateTeamSurvey,
} from '~src/modules/surveys/constants/actionTypes';
import {
  Survey,
  SurveysListResp,
  FetchSurveysParams,
  CreateUserPullSurveySubmitData,
  NewManagerSurveySubmitData,
  SurveyScheduleResponse,
  NewSurveySubmitData,
  NewTeamSurveySubmitData,
  CreateTeamSurveyResp,
} from '~src/modules/surveys/interfaces';
import { AsyncAction, ActionTypes } from '~src/services/apiMiddleware';

export const fetchSurveys = (
  params: FetchSurveysParams = {},
  surveyType?: SurveySubjectTypes,
  loadMore?: boolean,
  isSearch?: boolean,
  config?: AxiosRequestConfig,
): AsyncAction<SurveysListResp, { loadMore?: boolean, isSearch?: boolean }> => {
  const queryParams = queryString.stringify(params);
  let actionTypes: ActionTypes;
  switch (surveyType) {
    case SurveySubjectTypes.General:
      actionTypes = isSearch ? GetPulseSurveysSearch : GetPulseSurveys;
      break;
    case SurveySubjectTypes.Groups:
      actionTypes = isSearch ? GetGroupSurveysSearch : GetGroupSurveys;
      break;
    case SurveySubjectTypes.People:
      actionTypes = isSearch ? GetPeopleSurveysSearch : GetPeopleSurveys;
      break;
    default:
      actionTypes = GetSurveys;
  }
  const action: AsyncAction<SurveysListResp, { loadMore?: boolean, isSearch?: boolean }> = {
    types: actionTypes,
    payload: { loadMore, isSearch },
    callAPI: () => reportingApi.get(`/reports/company/surveys?${queryParams}`, config),
  };

  if (isSearch && (params.filterName ?? true)) {
    delete params.filterName;
    delete params.filterSubject;
    action.callAPI = () => defaultPulseCheckApi.getVisibleSurveys(params, config)
  }
  return action;
};

export const fetchVisibleSurveys = (
  params: FetchSurveysParams = {},
  surveyType?: SurveySubjectTypes,
  config?: AxiosRequestConfig,
): AsyncAction<SurveysListResp, undefined> => {
  let actionTypes: ActionTypes;
  switch (surveyType) {
    case SurveySubjectTypes.General:
      actionTypes = GetPulseSurveys;
      break;
    case SurveySubjectTypes.Groups:
      actionTypes = GetGroupSurveys;
      break;
    case SurveySubjectTypes.People:
      actionTypes = GetPeopleSurveys;
      break;
    default:
      actionTypes = GetSurveys;
  }
  delete params.filterName;
  delete params.filterSubject;

  const action: AsyncAction<SurveysListResp, undefined> = {
    types: actionTypes,
    callAPI: () => defaultPulseCheckApi.getVisibleSurveys(params, config),
  };

  return action;
};

export function copySurvey(id: string): AsyncAction<Survey, undefined> {
  return {
    types: CopySurvey,
    callAPI: () => pulseCheck.post(`/surveys/${id}/copy`),
  };
}

export function getSurvey(id: string): AsyncAction<Survey, {id: string}> {
  return {
    types: [GET_SURVEY_REQUEST, GET_SURVEY_SUCCESS, GET_SURVEY_FAILED],
    callAPI: () => defaultPulseCheckApi.getSurvey(id),
    payload: { id },
  };
}

export function addSurvey(survey: NewSurveySubmitData): AsyncAction<unknown, NewSurveySubmitData> {
  return {
    types: [AddSurvey.Request, AddSurvey.Success, AddSurvey.Failed],
    callAPI: () => defaultPulseCheckApi.postSurvey(survey),
    payload: survey,
  };
}

export function addManagersSurvey(surveyAndSchedule: NewManagerSurveySubmitData): AsyncAction<SurveyScheduleResponse, NewManagerSurveySubmitData> {
  return {
    types: [AddManagerSurvey.Request, AddManagerSurvey.Success, AddManagerSurvey.Failed],
    callAPI: () => defaultPulseCheckApi.postManagersSurvey(surveyAndSchedule),
    payload: surveyAndSchedule,
  };
}

export const addTeamSurvey = (data: NewTeamSurveySubmitData): AsyncAction<CreateTeamSurveyResp, undefined> => ({
  types: [CreateTeamSurvey.Request, CreateTeamSurvey.Success, CreateTeamSurvey.Failed],
  callAPI: () => defaultPulseCheckApi.postTeamSurvey(data),
});

export const createUserPullSurvey = (
  data: CreateUserPullSurveySubmitData,
): AsyncAction<SurveyScheduleResponse, undefined> => ({
  types: [CreateUserPullSurvey.Request, CreateUserPullSurvey.Success, CreateUserPullSurvey.Failed],
  callAPI: () => defaultPulseCheckApi.postUsersSurvey(data),
});

export function updateSurvey(id: string, formData: NewSurveySubmitData): AsyncAction<unknown, {id: string}> {
  return {
    types: [SAVE_TOPICS_FOR_SURVEY_REQUEST, SAVE_TOPICS_FOR_SURVEY_SUCCESS, SAVE_TOPICS_FOR_SURVEY_FAILED],
    callAPI: () => defaultPulseCheckApi.putSurvey(id, formData),
    payload: { id },
  };
}

export function deleteSurvey(id: string): AsyncAction<void, {id: string}> {
  return {
    types: DeleteSurvey,
    callAPI: () => defaultPulseCheckApi.deleteSurvey(id),
    payload: { id },
  };
}

export function editSurvey(id: string, formData: Record<string, unknown>): AsyncAction<unknown, {id: string, formData: Record<string, unknown>}> {
  return {
    types: EditSurvey,
    callAPI: () => defaultPulseCheckApi.editSurvey(id, formData),
    payload: { id, formData },
  };
}

export function resetSurveys() {
  return { type: RESET_SURVEYS };
}
